import React, { useEffect, useRef, useState, useMemo } from "react";
import "../../styles/About.css";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { Link } from 'react-router-dom';
import PeerCampusDemoModal from "../../components/PeerCampusDemoModal";
import ScrollToTopButton from "../../components/ScrollToTopButton";

const DEMO_PARTNER_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScvBbbyZIy9H-uIImuWbRrYSsjjylynEF-qPIuALQqHCo6Egg/viewform?usp=publish-editor";

/** Assets */
// We'll use optimized WebP variants generated into /imgs (public folder)
// Keep source-JPEG imports removed so the browser picks the WebP variants via srcSet


/** Small helper: reveal-on-scroll for any .reveal element */
function useRevealOnScroll() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/** Count-up animation when stats section enters the viewport */
function useStatCounters() {
  const startedRef = useRef(false);

  useEffect(() => {
    const section = document.querySelector(".stats-section");
    if (!section) return;

    const numbers = Array.from(section.querySelectorAll(".stat-number"));

    const animate = (el, target) => {
      const duration = 1200;
      const start = performance.now();
      const startVal = 0;

      const step = (now) => {
        const p = Math.min(1, (now - start) / duration);
        const val = Math.floor(startVal + (target - startVal) * p);
        el.textContent = val.toLocaleString() + "+";
        if (p < 1) requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting || startedRef.current) return;
        startedRef.current = true;

        numbers.forEach((n) => {
          const target = parseInt(n.getAttribute("data-target") || "0", 10);
          animate(n, target);
        });
      },
      { threshold: 0.25 }
    );

    io.observe(section);
    return () => io.disconnect();
  }, []);
}

/** Team data with photos and real links */
const team = [
  {
    name: "Ayush Pal",
    role: "Team Lead",
    // base name in public/imgs (generated by scripts/generate_images.py)
    photo: "team-ayush",
    socials: {
      linkedin: "https://www.linkedin.com/in/ayush-p-28918a25b",
      github: "https://github.com/Ayush777Pal",
      twitter: "https://x.com/PalAyush91604",
    },
  },
  {
    name: "Abhinav Chaurasia",
    role: "Frontend Developer",
    photo: "team-abhinav",
    socials: {
      linkedin: "https://www.linkedin.com/in/abhinavchaurasia-dev",
      github: "https://github.com/abhinavchaurasia-dev",
      twitter: "https://x.com/abhinavc_dev",
    },
  },
  {
    name: "Muklendra Pratap Rao",
    role: "Frontend Developer",
    photo: "team-muklendra",
    socials: {
      linkedin: "https://www.linkedin.com/in/muklendra-pratap-rao-53bab5279",
      github: "https://github.com/MuklendraPratapRao",
      twitter: "https://x.com/mr_Mukul78",
    },
  },
  {
    name: "Aditya Kant Kushwaha",
    role: "Backend Developer",
    photo: "team-aditya",
    socials: {
      linkedin: "https://www.linkedin.com/in/adityakantkushwaha",
      github: "https://github.com/Adityakantkushwaha",
      twitter: "https://x.com/AdiKantKushwaha",
    },
  },
];

const ABOUT_DEMO_TOKENS = {
  colors: {
    primary: "#016f57",
    secondary: "#019a78",
    accent: "#016f57",
    text: "#1F2937",
    textLight: "#64748B",
    light: "#F8FAFC",
    lightBlue: "rgba(1, 111, 87, 0.08)",
    success: "#019a78",
    gradient: "linear-gradient(135deg, #016f57 0%, #019a78 100%)",
  },
  borderRadius: {
    lg: "18px",
    xl: "24px",
  },
  shadows: {
    xl: "0 22px 48px rgba(1, 111, 87, 0.28)",
  },
};

export default function About() {
  useRevealOnScroll();
  useStatCounters();
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const demoFeatures = useMemo(() => [
    "Guided walkthrough of the AI doubt solver",
    "How clubs publish and promote campus events",
    "Peer-to-peer skill sharing and micro-internships",
    "Admin overview with privacy-first controls",
  ], []);

  return (
    <div className="about-page">
  <a href="#maincontent" className="skip-link">Skip to content</a>
  <Navbar />
  <main id="maincontent" role="main">

      {/* ========= Hero ========= */}
      <section className="hero-section" aria-labelledby="hero-heading">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text reveal">
              <h1 id="hero-heading">Your AI-Powered Campus Hub</h1>
              <p>
                PeerCampus brings together students, clubs, and mentors into one
                platform — powered by AI. Discover events, resolve doubts
                instantly, and grow your network with ease.
              </p>

              <div className="hero-buttons">
                <Link to="/login" className="btn-primary">
                  Get Started <span className="arrow">→</span>
                </Link>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setVideoModalOpen(true)}
                >
                  Watch Demo
                </button>
              </div>

              <div className="hero-stats">
                <div className="rating">
                  <span>⭐️⭐️⭐️⭐️⭐️</span>
                  <span>Trusted by 1,200+ students</span>
                </div>
                <div className="rating">🔒 Privacy-first • ⏱️ Instant AI Help</div>
              </div>
            </div>

            {/* Use real asset image instead of illustration icons */}
            <div className="hero-image reveal reveal-delay-1">
              <img
                src="/imgs/hero-about.webp"
                srcSet="/imgs/hero-about-480.webp 480w, /imgs/hero-about-768.webp 768w, /imgs/hero-about-1024.webp 1024w"
                sizes="(max-width: 768px) 100vw, 50vw"
                alt="Students collaborating on PeerCampus"
                className="hero-img"
                loading="lazy"
                width="460"
                height="300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ========= Mission / Vision / Core Values (single horizontal row) ========= */}
      <section className="mission-section">
        <div className="container">
          <div
            className="mission-grid"
            style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
          >
            <article className="mission-card reveal">
              <div className="mission-icon">🎯</div>
              <h3>Mission</h3>
              <p>
                Empower students through AI-driven, collaborative, and stress-free
                campus engagement.
              </p>
              <ul>
                <li>Smarter study workflows</li>
                <li>Real-time doubt resolution</li>
                <li>Community-led growth</li>
              </ul>
            </article>

            <article className="mission-card reveal reveal-delay-1">
              <div className="mission-icon">📈</div>
              <h3>Vision</h3>
              <p>
                To be the go-to hub where students, mentors, and clubs collaborate
                seamlessly.
              </p>
              <ul>
                <li>AI as a study companion</li>
                <li>Inclusive, vibrant communities</li>
                <li>Opportunities for everyone</li>
              </ul>
            </article>

            <article className="mission-card reveal reveal-delay-2">
              <div className="mission-icon">⚙️</div>
              <h3>Core Values</h3>
              <p>
                Champion empathy and trust so PeerCampus remains student-first and community-driven.
              </p>
              <ul>
                <li>Collaboration First</li>
                <li>Privacy &amp; Trust</li>
                <li>Continuous Growth</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ========= How It Works ========= */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="reveal">How PeerCampus Works</h2>

          <div className="work-grid">
            <div className="work-card reveal">
              <div className="work-icon student-icon">👩‍🎓</div>
              <h3>Students</h3>
              <p>
                Discover events, resolve study doubts, and connect with your campus
                community.
              </p>
            </div>

            <div className="work-card reveal reveal-delay-1">
              <div className="work-icon club-icon">🏛️</div>
              <h3>Clubs &amp; Societies</h3>
              <p>
                Manage club activities, host events, and engage members with
                streamlined tools.
              </p>
            </div>

            <div className="work-card reveal reveal-delay-2">
              <div className="work-icon admin-icon">🛠️</div>
              <h3>Admins</h3>
              <p>
                Oversee platform activity, ensure smooth onboarding, and support
                community growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========= Why Choose / Features (uniform cards, no dead links) ========= */}
      <section className="features-section">
        <div className="container">
          <header className="section-header reveal">
            <h2>Why Choose PeerCampus?</h2>
            <p>
              Revolutionary AI-powered features that redefine student life —
              smarter learning, seamless networking, and endless opportunities.
            </p>
          </header>

          <div className="features-grid">
            <article className="feature-card reveal">
              <div className="feature-icon">📘</div>
              <h3>AI-Powered Learning</h3>
              <p>
                24/7 AI guidance for study doubts, personalized insights, and smart
                recommendations.
              </p>
            </article>

            <article className="feature-card reveal reveal-delay-1">
              <div className="feature-icon">🔗</div>
              <h3>Smart Networking</h3>
              <p>
                Connect with peers, join relevant clubs, and grow your professional
                network.
              </p>
            </article>

            <article className="feature-card reveal reveal-delay-2">
              <div className="feature-icon">📚</div>
              <h3>Skill Development</h3>
              <p>
                Track your progress with workshops, self-paced lessons, and curated
                career-ready skills.
              </p>
            </article>
          </div>

          <div className="advanced-features">
            <div className="advanced-feature reveal">
              <h4>🧠 Smart Skill Hub</h4>
              <p>Learn from tailored programs and peer-powered learning resources.</p>
            </div>

            <div className="advanced-feature reveal reveal-delay-1">
              <h4>🌐 Global College Network</h4>
              <p>Collaborate with student groups and mentors beyond the globe.</p>
            </div>

            <div className="advanced-feature reveal reveal-delay-2">
              <h4>📱 Multi-Platform Experience</h4>
              <p>
                Consistent experiences across all platforms for active learning
                on the go.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========= Impact / Stats (colored cards + readable text) ========= */}
{/* <section className="stats-section">
  <div className="container">
    <h2 className="reveal">Impacting Students Nationwide</h2>
    <p className="reveal reveal-delay-1">
      Thousands of students are already transforming their campus experience
      with PeerCampus.
    </p>

    <div className="stats-grid">
      <div className="stat-card student reveal">
        <div className="stat-icon">👥</div>
        <div className="stat-number" data-target="1200">0+</div>
        <div className="stat-label">Active Students</div>
      </div>

      <div className="stat-card network reveal reveal-delay-1">
        <div className="stat-icon">💡</div>
        <div className="stat-number" data-target="3000">0+</div>
        <div className="stat-label">Resolved Doubts</div>
      </div>

      <div className="stat-card events reveal reveal-delay-2">
        <div className="stat-icon">🗂️</div>
        <div className="stat-number" data-target="250">0+</div>
        <div className="stat-label">Active Study Groups</div>
      </div>
    </div>
  </div>
</section> */}


      {/* ========= Team (single horizontal row, photos, clickable socials) ========= */}
      <section className="team-section" aria-labelledby="team-heading">
        <div className="container">
          <header className="section-header reveal">
            <h2 id="team-heading">Meet Our Team</h2>
            <p>
              Passionate individuals building PeerCampus for the next generation of
              students.
            </p>
          </header>

          <div
            className="team-grid"
            style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
          >
            {team.map((m) => (
              <article className="team-card reveal" key={m.name}>
                <div className="team-avatar">
                  <img
                    src={`/imgs/${m.photo}.webp`}
                    srcSet={`/imgs/${m.photo}-480.webp 480w, /imgs/${m.photo}-768.webp 768w, /imgs/${m.photo}-1024.webp 1024w`}
                    sizes="(max-width: 768px) 45vw, 160px"
                    alt={m.name}
                    className="team-photo"
                    loading="lazy"
                    width="100"
                    height="100"
                  />
                </div>
                <h3>{m.name}</h3>
                <p>{m.role}</p>

                <div className="social-links">
                  <a
                    href={m.socials.linkedin}
                    className="social-link"
                    aria-label={`${m.name} on LinkedIn`}
                    title="LinkedIn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8h4V24h-4V8zm7.5 0h3.8v2.2h.06c.53-1 1.83-2.2 3.77-2.2 4.03 0 4.77 2.65 4.77 6.1V24h-4v-7.1c0-1.7-.03-3.88-2.37-3.88-2.38 0-2.75 1.85-2.75 3.76V24h-4V8z" />
                    </svg>
                  </a>
                  <a
                    href={m.socials.github}
                    className="social-link"
                    aria-label={`${m.name} on GitHub`}
                    title="GitHub"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M12 .5C5.73.5.98 5.24.98 11.5c0 4.86 3.15 8.98 7.51 10.43.55.1.75-.23.75-.53v-2c-3.05.66-3.7-1.31-3.7-1.31-.5-1.27-1.22-1.6-1.22-1.6-.99-.68.08-.67.08-.67 1.1.08 1.68 1.14 1.68 1.14.98 1.67 2.56 1.19 3.18.9.1-.71.39-1.19.7-1.46-2.44-.28-5-1.22-5-5.42 0-1.2.43-2.17 1.14-2.94-.12-.28-.5-1.42.11-2.96 0 0 .94-.3 3.08 1.13.89-.25 1.85-.38 2.8-.38.95 0 1.9.13 2.8.38 2.14-1.43 3.08-1.13 3.08-1.13.61 1.54.23 2.68.11 2.96.71.77 1.14 1.74 1.14 2.94 0 4.21-2.56 5.14-5 5.41.4.34.75 1.02.75 2.06v3.05c0 .3.2.64.75.53 4.36-1.45 7.51-5.57 7.51-10.43C23.02 5.24 18.27.5 12 .5z" />
                    </svg>
                  </a>
                  <a
                    href={m.socials.twitter}
                    className="social-link"
                    aria-label={`${m.name} on X`}
                    title="Twitter / X"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M18.244 2H21l-6.56 7.49L22.5 22h-6.89l-5.39-7.07L3.5 22H1l6.99-7.98L1.5 2h6.89l4.86 6.37L18.244 2zm-1.2 18h1.51L8.91 4h-1.5l9.63 16z" />
                    </svg>
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ========= CTA ========= */}
  <section className="transform-section" aria-labelledby="transform-heading">
        <div className="container transform-content">
          <h2 id="transform-heading" className="reveal">Ready to Transform Your Campus Life?</h2>
          <p className="reveal reveal-delay-1">
            Join thousands of students using PeerCampus to enhance their learning,
            grow connections, and unlock new opportunities.
          </p>
          <p className="transform-subtext">
            ✓ Flexible Plans • ✓ 5-Minute Signup • ✓ 24/7 AI Support
          </p>

          <div className="transform-buttons">
            <a
              href={DEMO_PARTNER_FORM_URL}
              className="btn-secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Partner With Us
            </a>
          </div>
        </div>
      </section>
      </main>

      <ScrollToTopButton
        threshold={400}
        palette={{
          background: ABOUT_DEMO_TOKENS.colors.gradient,
          shadow: ABOUT_DEMO_TOKENS.shadows.xl,
          hoverShadow: "0 20px 40px rgba(1, 111, 87, 0.35)",
        }}
        ariaLabel="Back to top"
      />

      <PeerCampusDemoModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        designTokens={ABOUT_DEMO_TOKENS}
        features={demoFeatures}
        title="🎥 Explore PeerCampus in Minutes"
        subtitle="See the student, club, and admin journeys in a single immersive walkthrough"
      />

      <Footer />
    </div>
  );
}
